/**
 * Guards de rota para a SPA web.
 *
 * <SessionLoader>              — skeleton minimal enquanto status indefinido.
 * <RequireAuth>                — exige sessao autenticada, senao redirect /sign-in.
 * <RequireSuperAdmin>          — exige isSuperAdmin, senao redirect /escritorio.
 * <RequireTenant>               — exige tenantId, senao redirect /sem-escritorio
 *                                 (ou /admin se for super_admin).
 * <RequireModule>              — exige modulo habilitado (owner/admin sempre passa),
 *                                 senao redirect /escritorio (dashboard).
 * <RequireActiveSubscription>  — bloqueia toda a area logada quando o tenant
 *                                 esta inadimplente, exceto a propria pagina
 *                                 de assinatura (onde ele regulariza).
 */

import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './auth-store';
import { usePermissions, type AppModule } from './use-permissions';
import BillingBlockedPage from '../pages/billing-blocked';

export function SessionLoader(): ReactElement {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      role="status"
      aria-live="polite"
      aria-label="Carregando sessão"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-current border-t-transparent animate-spin opacity-60" />
        <span className="text-sm opacity-60">Carregando...</span>
      </div>
    </div>
  );
}

interface RequireAuthProps {
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function RequireAuth({
  children,
  redirectTo = '/sign-in',
  fallback,
}: RequireAuthProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'unknown' || status === 'authenticating') {
    return <>{fallback ?? <SessionLoader />}</>;
  }
  if (status === 'unauthenticated') {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  return <>{children}</>;
}

interface RequireSuperAdminProps {
  children: ReactNode;
  redirectTo?: string;
}

export function RequireSuperAdmin({
  children,
  redirectTo = '/escritorio',
}: RequireSuperAdminProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  if (status === 'unknown' || status === 'authenticating') {
    return <SessionLoader />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />;
  }
  if (!isSuperAdmin) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

interface RequireTenantProps {
  children: ReactNode;
  redirectTo?: string;
}

interface RequireAdminProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Exige que o usuario seja owner ou admin do tenant.
 * Usado para proteger /configuracoes/* e itens de menu restritos.
 */
export function RequireAdmin({
  children,
  redirectTo = '/escritorio',
}: RequireAdminProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const { isAdmin } = usePermissions();

  if (status === 'unknown' || status === 'authenticating') {
    return <SessionLoader />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />;
  }
  if (!isAdmin) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

interface RequireModuleProps {
  children: ReactNode;
  module: AppModule;
  redirectTo?: string;
}

/**
 * Protege rotas por modulo. Owner/admin passam sempre.
 * Member sem o modulo e redirecionado para o dashboard.
 *
 * Deve ser usado dentro de RequireAuth + RequireTenant (presupoe sessao ativa).
 */
export function RequireModule({
  children,
  module,
  redirectTo = '/escritorio',
}: RequireModuleProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const { hasModule } = usePermissions();

  if (status === 'unknown' || status === 'authenticating') {
    return <SessionLoader />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />;
  }
  if (!hasModule(module)) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

export function RequireTenant({
  children,
  redirectTo = '/sem-escritorio',
}: RequireTenantProps): ReactElement {
  const status = useAuthStore((s) => s.status);
  const tenantId = useAuthStore((s) => s.tenantId);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);

  if (status === 'unknown' || status === 'authenticating') {
    return <SessionLoader />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />;
  }
  if (!tenantId) {
    // Super admin sem tenant ativo cai no painel admin. Membros sem tenant
    // (caso degenerado: invite revogado, membership removida) viram para
    // pagina informativa — nao podem criar tenant, isso e privilegio
    // exclusivo de super_admin via /admin/clientes/novo.
    if (isSuperAdmin) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

/** Rota exclusiva onde o tenant bloqueado ainda consegue navegar — e onde ele regulariza. */
const SUBSCRIPTION_PATH = '/escritorio/configuracoes/assinatura';

interface RequireActiveSubscriptionProps {
  children: ReactNode;
}

/**
 * Envolve o shell logado do escritorio (ver App.tsx, Route pai do
 * DashboardShell). Quando o tenant esta bloqueado por inadimplencia
 * (`billingBlocked`, populado por /me ou forcado por um 402
 * SUBSCRIPTION_BLOCKED em qualquer request — ver api-client/auth-store),
 * renderiza a tela dedicada de bloqueio no lugar do app inteiro — EXCETO
 * na propria pagina de assinatura, unico lugar onde ele pode regularizar.
 *
 * Nao usa <Navigate> (nao forca redirect de URL): a tela de bloqueio e
 * renderizada diretamente, preservando a rota que o usuario tentou abrir
 * (se ele conseguir regularizar e a pagina reavaliar, volta pra onde
 * estava sem navegacao extra).
 */
export function RequireActiveSubscription({
  children,
}: RequireActiveSubscriptionProps): ReactElement {
  const billingBlocked = useAuthStore((s) => s.billingBlocked);
  const location = useLocation();

  if (billingBlocked && location.pathname !== SUBSCRIPTION_PATH) {
    return <BillingBlockedPage />;
  }
  return <>{children}</>;
}
